import _extends from "@babel/runtime/helpers/esm/extends";
import { MuiAlertOverrides } from './ThemeOverrides/alertOverrides';
import { coreBreakpointValues } from './ThemeOverrides/breakpointVariants';
import { MuiCheckboxOverrides } from './ThemeOverrides/checkboxOverrides';
import { MuiChipOverrides } from './ThemeOverrides/chipOverrides';
import { MuiFormHelperTextOverrides, MuiInputOverrides } from './ThemeOverrides/inputOverrides';
import { MuiRadioOverrides } from './ThemeOverrides/radioOverrides';
import { MuiSelectOverrides } from './ThemeOverrides/selectOverrides';
import { MuiSnackbarContentOverrides } from './ThemeOverrides/snackbarContentOverrides';
import { MuiTableContainerOverrides } from './ThemeOverrides/TableContainerOverrides';
import { MuiTooltipOverrides } from './ThemeOverrides/tooltipOverrides';
import { CoreTypographyThemeVariants } from './ThemeOverrides/typographyVariants';
export var commonColors = {
  black: '#222222',
  darkBlue: '#24466C',
  green: '#008A5E',
  lightBlue: '#0374B3',
  orange: '#F04923',
  purple: '#2E2E81',
  white: '#ffffff'
};
export var coreThemeColors = {
  primary: {
    contrastText: '#FFF',
    dark: '#006562',
    main: '#0C938F',
    light: '#55C4BF',
    outlinedHoverBackground: '#EEF8F6',
    outlinedRestingBorder: '#55C4BF',
    containedHoverBackground: '#006562'
  },
  secondary: {
    contrastText: '#FFF',
    dark: '#69075C',
    main: '#8F2881',
    light: '#FF85CE',
    outlinedHoverBackground: '#F7EFF6',
    outlinedRestingBorder: '#FF85CE',
    containedHoverBackground: '#67075A'
  },
  error: {
    contrastText: '#FFF',
    dark: '#C72929',
    main: '#D33131',
    light: '#EF5552',
    outlinedHoverBackground: '#D331310A',
    outlinedRestingBorder: '#FFA3A3',
    containedHoverBackground: '#B33333',
    background: '#FFECEC',
    content: '#661D1D'
  },
  warning: {
    contrastText: '#FFF',
    dark: '#E55000',
    main: '#ED6C02',
    light: '#FF9900',
    outlinedHoverBackground: '#FEF4EB',
    containedHoverBackground: '#B36C32',
    outlinedRestingBorder: '#F5B580',
    background: '#FFF4EB',
    content: '#643C1B'
  },
  info: {
    contrastText: '#FFF',
    dark: '#01599D',
    main: '#0287CF',
    light: '#02A6F2',
    outlinedHoverBackground: '#EFF6FF',
    containedHoverBackground: '#02517E',
    outlinedRestingBorder: '#2063B3',
    background: '#EAF4FF',
    content: '#123966'
  },
  success: {
    contrastText: '#FFF',
    dark: '#1C5F20',
    main: '#2F7F33',
    light: '#4CAE4F',
    outlinedHoverBackground: '#EDFBF3',
    containedHoverBackground: '#006142',
    outlinedRestingBorder: '#88E6B2',
    background: '#E7FAF0',
    content: '#085229'
  },
  common: {
    white: '#ffffff',
    black: '#222222'
  },
  text: {
    disabled: 'rgba(0, 0, 0, 0.38)',
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)'
  },
  action: {
    active: 'rgba(0, 0, 0, 0.54)',
    activatedOpacity: 0.38,
    selected: 'rgba(0, 0, 0, 0.08)',
    selectedOpacity: 0.08,
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    disabledOpacity: 0.26,
    hover: 'rgba(0,0,0,0.04)',
    hoverOpacity: 0.08,
    focus: 'rgba(0,0,0,0.12)',
    focusOpacity: 0.12
  },
  mode: 'light',
  contrastThreshold: 3,
  tonalOffset: 0.2,
  divider: 'rgba(0, 0, 0, 0.12)',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
    A100: '#D5D5D5',
    A200: '#AAAAAA',
    A400: '#303030',
    A700: '#616161'
  },
  background: {
    paper: '#FFF',
    "default": '#FFF'
  }
};
export var CORE_THEME_VARIANT_MAPPING = {
  body1: 'p',
  body2: 'p',
  button: 'button',
  caption: 'caption',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  overline: 'p',
  subtitle1: 'h2',
  subtitle2: 'h4'
};

//TODO: need to add the Aptitude theme colors & overrides
export var themeDefaults = {
  spacing: [1, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64, 80, 96, 128, 160, 192, 224, 256],
  typography: CoreTypographyThemeVariants,
  shape: {
    borderRadius: 4
  },
  breakpoints: {
    values: coreBreakpointValues
  },
  chartsColorPalette: {
    blue: {
      main: '#0D6EFD',
      lighten20: '#3D8BFD',
      lighten40: '#6EA8FE',
      lighten60: '#9EC5FE',
      lighten80: '#CFE2FF',
      darken20: '#0A58CA'
    },
    indigo: {
      main: '#6610F2',
      lighten80: '#E0CFFC',
      lighten60: '#C29FFA',
      lighten40: '#A370F7',
      lighten20: '#8540F5',
      darken20: '#520DC2'
    },
    purple: {
      main: '#6F42C1',
      lighten80: '#E2D9F3',
      lighten60: '#C5B3E6',
      lighten40: '#A98EDA',
      lighten20: '#8C68CD',
      darken20: '#59359A'
    },
    pink: {
      main: '#D63384',
      lighten20: '#DE5C9D',
      lighten40: '#E685B5',
      lighten60: '#EFADCE',
      lighten80: '#F7D6E6',
      darken20: '#AB296A'
    },
    red: {
      main: '#DC3545',
      lighten20: '#E35D6A',
      lighten40: '#EA868F',
      lighten60: '#F1AEB5',
      lighten80: '#F8D7DA',
      darken20: '#B02A37'
    },
    orange: {
      main: '#FD7E14',
      lighten20: '#FD9843',
      lighten40: '#FEB272',
      lighten60: '#FECBA1',
      lighten80: '#FFE5D0',
      darken20: '#CA6510'
    },
    yellow: {
      main: '#FFC107',
      lighten20: '#FFCD39',
      lighten40: '#FFDA6A',
      lighten60: '#FFE69C',
      lighten80: '#FFF3CD',
      darken20: '#CC9A06'
    },
    green: {
      main: '#198754',
      lighten20: '#479F76',
      lighten40: '#75B798',
      lighten60: '#A3CFBB',
      lighten80: '#D1E7DD',
      darken20: '#146C43'
    },
    teal: {
      main: '#20C997',
      lighten20: '#4DD4AC',
      lighten40: '#79DFC1',
      lighten60: '#A6E9D5',
      lighten80: '#D2F4EA',
      darken20: '#1AA179'
    },
    cyan: {
      main: '#0DCAF0',
      lighten20: '#3DD5F3',
      lighten40: '#6EDFF6',
      lighten60: '#9EEAF9',
      lighten80: '#CFF4FC',
      darken20: '#0AA2C0'
    }
  },
  palette: _extends({}, coreThemeColors),
  components: {
    MuiCssBaseline: {
      styleOverrides: "\n        @font-face {\n          font-display: swap;\n          font-family: 'Roboto', sans-serif;\n          font-style: normal;\n          font-weight: 400;\n        }\n        body {\n          background-color: ".concat(coreThemeColors.background["default"], "\n        }\n      ")
    },
    MuiChip: MuiChipOverrides,
    MuiFormHelperText: MuiFormHelperTextOverrides,
    MuiTooltip: MuiTooltipOverrides,
    MuiInput: MuiInputOverrides,
    MuiSelect: MuiSelectOverrides,
    MuiSnackbarContent: MuiSnackbarContentOverrides,
    MuiTableContainer: MuiTableContainerOverrides,
    MuiAlert: MuiAlertOverrides,
    MuiCheckbox: MuiCheckboxOverrides,
    MuiRadio: MuiRadioOverrides
  }
};
export default themeDefaults;